import React from "react";
import useSWR, { mutate } from "swr";
import api, { API_BASE } from "@/lib/api";
import { gbp, ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileText, Download, CheckCircle2, Loader2 } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const statusColor = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  approved: "bg-blue-100 text-blue-700 border-blue-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const Invoices = () => {
  const { data, isLoading } = useSWR("/invoices", fetcher);
  const { data: periods } = useSWR("/pay-periods", fetcher);
  const [generating, setGenerating] = React.useState(false);

  const genAll = async () => {
    const open = (periods || []).find((p) => p.status === "open");
    if (!open) {
      toast.error("No open pay period");
      return;
    }
    setGenerating(true);
    try {
      const { data: res } = await api.post(`/invoices/generate?period_id=${open.id}`);
      toast.success(`Generated ${res.count} invoice(s) for ${open.name}`);
      mutate("/invoices");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generate failed");
    }
    setGenerating(false);
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/invoices/${id}/mark-paid`);
      toast.success("Marked as paid");
      mutate("/invoices");
    } catch {
      toast.error("Update failed");
    }
  };

  const downloadPdf = (inv) => {
    // Open PDF in new tab — cookies carry auth
    window.open(`${API_BASE}/invoices/${inv.id}/pdf`, "_blank");
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Payroll cycle</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">Per-driver invoices with PDF export.</p>
        </div>
        <Button
          onClick={genAll}
          disabled={generating}
          className="bg-brand-red hover:bg-brand-red-hover"
          data-testid="generate-invoices-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
          Generate for current period
        </Button>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading…</div>}

      <Card className="rounded-2xl border-brand-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Invoice #</th>
                <th className="text-left px-4 py-3">Driver</th>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-left px-4 py-3">Pay date</th>
                <th className="text-right px-4 py-3">Net</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data || []).map((inv) => (
                <tr key={inv.id} data-testid={`invoice-${inv.number}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{inv.number}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-brand-text">{inv.driver_name}</div>
                    <div className="text-[11px] font-mono text-slate-500">{inv.driver_code}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{inv.period_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{ddmmyyyy(inv.payment_date)}</td>
                  <td className="px-4 py-2.5 text-right font-heading font-bold">{gbp(inv.net_pay)}</td>
                  <td className="px-4 py-2.5 text-center">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusColor[inv.status] || ""}`}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv)} data-testid={`inv-pdf-${inv.number}`}>
                        <Download className="w-3.5 h-3.5" />
                      </Button>
                      {inv.status !== "paid" && (
                        <Button size="sm" variant="ghost" onClick={() => markPaid(inv.id)} data-testid={`inv-paid-${inv.number}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {(data || []).length === 0 && !isLoading && (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-slate-500 text-sm">
                    <FileText className="w-6 h-6 mx-auto mb-2 opacity-40" />
                    No invoices yet. Click &ldquo;Generate for current period&rdquo; to create them.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default Invoices;
